// "Link teilen" on the owner's meeting page: the meeting's magic
// link, owner only.
//
// - "Link erstellen" asks the server for a new link; a live one stops (one
//   link per meeting). The token is in that answer only (the server keeps its
//   SHA-256), so the page shows the full URL once, to copy, and never again.
// - "Gültig bis …" and "Verlängern" (the same URL, valid for the full span
//   from now) and "Widerrufen" come from the server's `link` on the meeting.
// - "Neuer Link" and "Widerrufen" stop a link the owner may already have
//   sent, so while it still works each asks first, like "Meeting löschen".
// - "Änderungen über den Link erscheinen im Projekt unter deinem Namen."

import { useEffect, useState } from "react";

import type { MeetingLinkStateDto } from "@loom/shared/meetings";

import { useFormatDate } from "../../i18n/format";
import { useT, type MessageKey } from "../../i18n/index.js";

import { meetingLinkUrl } from "./linkMeetingApi";
import {
  createMeetingLink,
  failureOf,
  renewMeetingLink,
  revokeMeetingLink,
} from "./meetingApi";
import { failureKeyOf } from "./meetingCopy";

const DAY_FORMAT: Intl.DateTimeFormatOptions = { dateStyle: "medium" };

export function MeetingShareLink({
  meetingId,
  link,
  onChanged,
  questionsOpen = false,
}: {
  readonly meetingId: string;
  /** The live (not revoked) link, or null. */
  readonly link: MeetingLinkStateDto | null;
  /** After a create, renew or revoke: the page reloads the meeting. */
  readonly onChanged: () => void;
  /** Some issue still has open questions (refine light): the reason to send the link now. */
  readonly questionsOpen?: boolean;
}) {
  const t = useT();
  const day = useFormatDate(DAY_FORMAT);
  const [shownUrl, setShownUrl] = useState<string | null>(null);
  const [state, setState] = useState<MeetingLinkStateDto | null>(link);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ readonly key: MessageKey; readonly bad: boolean } | null>(null);
  const [copied, setCopied] = useState(false);
  // Which link-ending action waits for its confirm line.
  const [confirming, setConfirming] = useState<"replace" | "revoke" | null>(null);
  // The server's answer after a reload wins over this page's own last step.
  const linkExpiresAt = link?.expiresAt ?? null;
  const linkExpired = link?.expired ?? false;
  useEffect(() => {
    setState(linkExpiresAt === null ? null : { expiresAt: linkExpiresAt, expired: linkExpired });
  }, [linkExpiresAt, linkExpired]);
  const current = state;

  const run = (fn: () => Promise<void>): void => {
    if (busy) return;
    setBusy(true);
    setNote(null);
    setConfirming(null);
    fn()
      .catch((err: unknown) => setNote({ key: failureKeyOf(failureOf(err)), bad: true }))
      .finally(() => setBusy(false));
  };

  const create = (): void =>
    run(async () => {
      const made = await createMeetingLink(meetingId);
      setState({ expiresAt: made.expiresAt, expired: made.expired });
      setShownUrl(meetingLinkUrl(window.location.origin, made.token));
      setCopied(false);
      onChanged();
    });

  const renew = (): void =>
    run(async () => {
      setState(await renewMeetingLink(meetingId));
      onChanged();
    });

  const revoke = (): void =>
    run(async () => {
      await revokeMeetingLink(meetingId);
      setShownUrl(null);
      setState(null);
      setNote({ key: "meetings.share.revoked", bad: false });
      onChanged();
    });

  // A link that still works is one the owner may have sent: ask first.
  const live = current !== null && !current.expired;
  const ask = (action: "replace" | "revoke", go: () => void) => (): void => {
    if (live) setConfirming(action);
    else go();
  };

  const copy = (): void => {
    if (shownUrl === null) return;
    void navigator.clipboard?.writeText(shownUrl).then(
      () => setCopied(true),
      () => setCopied(false),
    );
  };

  return (
    <section className="meet-section" aria-label={t("meetings.share.title")}>
      <h2 className="meet-section-title">{t("meetings.share.title")}</h2>
      <p className="focus-help">{t("meetings.share.lead")}</p>
      {shownUrl !== null && (
        <div className="meet-share">
          <input
            className="meet-edit-input"
            readOnly
            aria-label={t("meetings.share.linkAria")}
            value={shownUrl}
            onFocus={(e) => e.currentTarget.select()}
          />
          <button type="button" className="focus-btn" onClick={copy}>
            {t(copied ? "meetings.share.copied" : "meetings.share.copy")}
          </button>
          <p className="meet-meta">{t("meetings.share.once")}</p>
        </div>
      )}
      {current !== null && (
        <p className="meet-meta">
          {t(current.expired ? "meetings.share.expired" : "meetings.share.until", {
            date: day(current.expiresAt),
          })}
        </p>
      )}
      <p className="meet-meta">{t("meetings.share.asOwner")}</p>
      {questionsOpen && <p className="meet-meta">{t("meetings.share.questions")}</p>}
      {confirming !== null && live ? (
        <div
          className="meet-confirm"
          role="group"
          aria-label={t(confirming === "replace" ? "meetings.share.replace" : "meetings.share.revoke")}
        >
          <p className="focus-help">{t("meetings.share.stopConfirm")}</p>
          <div className="meet-actions">
            <button
              type="button"
              className="focus-btn"
              disabled={busy}
              onClick={confirming === "replace" ? create : revoke}
            >
              {t(confirming === "replace" ? "meetings.share.replace" : "meetings.share.revoke")}
            </button>
            <button type="button" className="focus-btn is-ghost" disabled={busy} onClick={() => setConfirming(null)}>
              {t("common.cancel")}
            </button>
          </div>
        </div>
      ) : (
        <div className="meet-actions">
          <button
            type="button"
            className={current === null ? "focus-btn" : "focus-btn is-ghost"}
            disabled={busy}
            onClick={current === null ? create : ask("replace", create)}
          >
            {t(current === null ? "meetings.share.create" : "meetings.share.replace")}
          </button>
          {current !== null && (
            <>
              <button type="button" className="focus-btn is-ghost" disabled={busy} onClick={renew}>
                {t("meetings.share.renew")}
              </button>
              <button type="button" className="focus-btn is-ghost" disabled={busy} onClick={ask("revoke", revoke)}>
                {t("meetings.share.revoke")}
              </button>
            </>
          )}
        </div>
      )}
      {current !== null && <p className="meet-meta">{t("meetings.share.replaceNote")}</p>}
      {note !== null && (
        <div className={note.bad ? "focus-error" : "focus-help"} role={note.bad ? "alert" : "status"}>
          {t(note.key)}
        </div>
      )}
    </section>
  );
}
